/**
 * The edit pages (Amenities, Photos, Pricing, House Rules…) are shared by every flow and all end by
 * returning the host to a Review page. To put the host back on the exact page — of the exact flow —
 * they started editing from, the starting page remembers where to return to, per property.
 *
 * With nothing remembered, edits end on the normal Review (acknowledgement) page, as before.
 */
const KEY = 'editReturnTarget';

/** Call when an Edit button is pressed: `returnPath` is the page the host is leaving. */
export const startImportEdit = (
  propertyId: number | string | null | undefined,
  returnPath: string,
): void => {
  try {
    if (propertyId != null) {
      sessionStorage.setItem(KEY, JSON.stringify({ propertyId: String(propertyId), returnPath }));
    }
  } catch (_) {
    /* storage unavailable */
  }
};

/** Call once the host is back on a Review/Verify page, so a later plain edit isn't redirected. */
export const clearImportEdit = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (_) {
    /* storage unavailable */
  }
};

/**
 * The page this property's edit started from, or null when the host isn't in an edit started from a
 * Review/Verify page. For edit pages that save on their own and so can return straight away.
 */
export const getEditReturnPath = (propertyId: number | string | null | undefined): string | null => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (saved && propertyId != null && saved.propertyId === String(propertyId) && saved.returnPath) {
      return saved.returnPath;
    }
  } catch (_) {
    /* storage unavailable */
  }
  return null;
};

/**
 * When the host is in an edit that started from a "Let us list it for you" page, which of its tabs to
 * highlight on the shared edit pages: Review for an edit started there, Verify for one started on the
 * Verify / What-did-we-miss pages. Null for any other edit (the normal List-it-yourself tabs).
 */
export const getImportEditSection = (): number | null => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    const path = String(saved?.returnPath || '');
    if (path.startsWith('/host/import-listing')) return 1;
    if (path.includes('flow=import')) return 3;
  } catch (_) {
    /* storage unavailable */
  }
  return null;
};

/** Where an edit page goes once it is done: the page this property's edit started from. */
export const reviewPathAfterEdit = (propertyId: number | string | null | undefined): string => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (saved && propertyId != null && saved.propertyId === String(propertyId) && saved.returnPath) {
      return saved.returnPath;
    }
  } catch (_) {
    /* storage unavailable */
  }
  return `/host/acknowledgement?id=${propertyId}`;
};
